import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const ServiceCard = ({ icon, title, bullets }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all">
    <div className="w-12 h-12 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center mb-5">
      <span className="text-2xl">{icon}</span>
    </div>
    <h3 className="text-xl font-bold text-navy-800 font-heading mb-4">{title}</h3>
    <ul className="space-y-2.5">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-2.5 text-slate-600 text-sm">
          <span className="text-gold-500 mt-0.5 shrink-0 font-bold">›</span>
          {b}
        </li>
      ))}
    </ul>
  </div>
);

const SERVICES = [
  {
    icon: '🛡️',
    title: 'Next-Gen Cybersecurity & Resilience',
    bullets: [
      'Zero Trust & Identity-First Security — continuous verification, strict access management, and ZTNA to eliminate lateral threat movement',
      'DevSecOps Pipeline Integration — automated security scanning, vulnerability analysis, and compliance checks embedded in CI/CD workflows',
      'Continuous Threat Exposure Management (CTEM) — automated vulnerability assessment and telemetry analysis to predict and remediate systemic flaws',
    ],
  },
  {
    icon: '☁️',
    title: 'Cloud-Native & Application Development',
    bullets: [
      'Microservices & System Architecture — resilient, distributed systems utilizing secure, containerized frameworks',
      'Cloud Migration & Optimization — transitioning enterprise workloads to public, private, or hybrid clouds with rigorous data isolation',
      'Cross-Platform Mastery — full operational engineering across hardened Enterprise Linux (Unix) and Windows Server ecosystems',
    ],
  },
  {
    icon: '📊',
    title: 'Advanced Data Engineering & AI Analytics',
    bullets: [
      'Secure Data Warehousing — designing secure data pipelines and modern analytics engines with compliance, data masking, and absolute privacy',
      'Business intelligence transformation from massive, complex federal datasets into actionable insights',
    ],
  },
  {
    icon: '⚙️',
    title: 'Engineering Methodologies',
    bullets: [
      'Agile & Scrum — rapid, iterative feature delivery paired with continuous security testing',
      'Kanban — optimizing high-velocity operational workflows and maximizing engineering throughput',
      'Hybrid / Hardened Waterfall — tailored for complex, high-compliance, or fixed-scope federal milestones requiring strict regulatory documentation',
    ],
  },
];

const TECH_STACK = [
  { category: 'Cybersecurity & Guardrails', tools: 'WAAP, SIEM/SOAR, IAM, DAST/SAST' },
  { category: 'Cloud & Containers', tools: 'AWS, Microsoft Azure, Docker, Kubernetes (K8s)' },
  { category: 'Languages & Frameworks', tools: 'Python, Java, C#, C++, Go, Rust, TypeScript' },
  { category: 'Data & Databases', tools: 'Oracle, PostgreSQL, Snowflake, MongoDB' },
  { category: 'CI/CD & DevOps', tools: 'Git, GitHub Actions, GitLab CI, Terraform' },
  { category: 'Collaboration & ITSM', tools: 'Jira, Confluence' },
];

const FRAMEWORKS = [
  'FISMA', 'NIST 800-53', 'NIST 800-171', 'CMMC', 'FISCAM', 'SSAE 18',
  'FedRAMP', 'RMF', 'SOC 2', 'Zero Trust', 'CTEM', 'DevSecOps',
];

const InformationTechnology = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">IT Services</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Service Line</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading leading-tight max-w-3xl">
            IT, Cloud &amp; Cybersecurity
          </h1>
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
            Next-generation engineering and uncompromising security — DTX builds resilient digital infrastructure
            bridging complex software development with proactive, defensive engineering and hardened cloud architectures.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Our Capabilities</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">What We Deliver</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {SERVICES.map((s) => (
              <ServiceCard key={s.title} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack table */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Technical Ecosystem</p>
            <h2 className="text-3xl font-black text-navy-800 font-heading">Our Technology Stack</h2>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy-800">
                  <th className="py-3 px-6 text-left text-xs font-bold text-gold-400 uppercase tracking-wider w-56">Category</th>
                  <th className="py-3 px-6 text-left text-xs font-bold text-gold-400 uppercase tracking-wider">Technologies, Tools & Frameworks</th>
                </tr>
              </thead>
              <tbody>
                {TECH_STACK.map(({ category, tools }, i) => (
                  <tr key={category} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <td className="py-4 px-6 font-bold text-navy-800">{category}</td>
                    <td className="py-4 px-6 text-slate-600">{tools}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Compliance frameworks */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Standards & Frameworks</p>
            <h2 className="text-3xl font-black text-navy-800 font-heading">Compliance We Support</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {FRAMEWORKS.map((f) => (
              <span
                key={f}
                className="bg-slate-50 border border-slate-200 text-navy-700 px-4 py-2 rounded-full text-sm font-semibold hover:border-gold-300 hover:bg-gold-50 transition-colors"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black text-white font-heading mb-4">
            Need IT Support?
          </h2>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto">
            Reach out to discuss your agency's technology requirements and how DTX can help you meet them.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold transition-colors shadow-lg"
          >
            Get in Touch
          </Link>
        </div>
      </section>

    </div>
  );
};

export default InformationTechnology;
